import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase } from './creative-api';

/**
 * The "New look" of the brand's banners: the art its latest website regeneration (Art Style, "Regenerate website
 * art") draws in the brand's own style, banner by banner, before players see it. Read-only here: it goes live when the
 * site is switched to it on the Art Style page. A banner's id is its casino-creation deployment's uuid, which is how
 * spark-creatives names each banner the regeneration covers.
 *
 * A regeneration in place (the site already shows the brand's style) has no separate new look to preview: each
 * banner's art replaces the live banner as it is drawn, so its banners are only `redrawing` or `redrawn`.
 */

/** A regeneration's phase, and a banner's new art in it: being drawn, drawn, or not drawn by a run that ended. */
export type NewLookPhase = 'drawing' | 'ready' | 'failed';

/** A banner's new art: a {@link NewLookPhase}, or in a regeneration in place, its live art being redrawn or redrawn. */
export type NewLookStatus = NewLookPhase | 'redrawing' | 'redrawn';

export interface NewLook {
  /** The regeneration's job ('' when there is no new look). */
  jobId: string;
  /** The regeneration's phase; '' when there is no new look to show. */
  phase: '' | NewLookPhase;
  /** Each banner the regeneration covers, by banner id. */
  byBanner: Record<string, NewLookStatus>;
}

export const NO_NEW_LOOK: NewLook = { jobId: '', phase: '', byBanner: {} };

const isPhase = (v: unknown): v is NewLookPhase => v === 'drawing' || v === 'ready' || v === 'failed';

const isStatus = (v: unknown): v is NewLookStatus => isPhase(v) || v === 'redrawing' || v === 'redrawn';

/** Whether a banner's status is a new look to preview (not a regeneration in place, which redraws the live art). */
export const isNewLookPhaseStatus = (status: NewLookStatus): status is NewLookPhase => isPhase(status);

/** The banners a regeneration in place has redrawn, as one sorted key ('' when none). */
export const redrawnKey = (look: NewLook): string =>
  Object.keys(look.byBanner)
    .filter((id) => look.byBanner[id] === 'redrawn')
    .sort()
    .join('|');

/** Whether the redrawn banners `next` holds include one that `prev` did not (keys from {@link redrawnKey}). */
export const gainedRedrawn = (prev: string, next: string): boolean => {
  if (next === '') return false;
  const had = new Set(prev === '' ? [] : prev.split('|'));
  return next.split('|').some((id) => !had.has(id));
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The service's new look as the page uses it; anything malformed reads as no new look. */
export const parseNewLook = (raw: unknown): NewLook => {
  if (!raw || typeof raw !== 'object') return NO_NEW_LOOK;
  const r = raw as Record<string, unknown>;
  const phase = isPhase(r.phase) ? r.phase : '';
  if (phase === '') return NO_NEW_LOOK;
  const byBanner: Record<string, NewLookStatus> = {};
  for (const d of Array.isArray(r.deployments) ? r.deployments : []) {
    const dep = (d ?? {}) as Record<string, unknown>;
    const id = str(dep.deployment_uuid);
    if (id && isStatus(dep.status)) byBanner[id] = dep.status;
  }
  return { jobId: str(r.job_id), phase, byBanner };
};

/** One banner's new art by format slot (e.g. `ar21_keyright` → url); empty until it is ready. */
export const parseNewLookFormats = (raw: unknown): Record<string, string> => {
  const formats = raw && typeof raw === 'object' ? (raw as Record<string, unknown>).formats : undefined;
  const out: Record<string, string> = {};
  if (!formats || typeof formats !== 'object') return out;
  for (const [slot, url] of Object.entries(formats as Record<string, unknown>)) {
    if (slot && typeof url === 'string' && url) out[slot] = url;
  }
  return out;
};

const newLookPath = (tenantId: number, suffix = ''): string =>
  `${creativesServiceBase()}/creatives/tenant/${encodeURIComponent(String(tenantId))}/site-regen/new-look${suffix}`;

/** The brand's new look; null when it could not be read (the caller keeps what it shows). */
export const fetchNewLook = async (tenantId: number, signal?: AbortSignal): Promise<NewLook | null> => {
  if (!creativesServiceBase()) return null;
  try {
    const res = await axios.get(newLookPath(tenantId), creativesRequestConfig(signal));
    return res.status === 200 ? parseNewLook(res.data) : null;
  } catch {
    return null;
  }
};

/** One banner's new art by format slot; null when it could not be read. */
export const fetchNewLookFormats = async (
  tenantId: number,
  bannerId: string,
  signal?: AbortSignal,
): Promise<Record<string, string> | null> => {
  if (!creativesServiceBase()) return null;
  try {
    const res = await axios.get(
      newLookPath(tenantId, `/${encodeURIComponent(bannerId)}`),
      creativesRequestConfig(signal),
    );
    return res.status === 200 ? parseNewLookFormats(res.data) : null;
  } catch {
    return null;
  }
};
